import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Navigate } from 'react-router-dom';
import { Package, Mail, User } from 'lucide-react';
import { PINPad } from '../components/forms/PINPad';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAuthStore } from '../stores/authStore';

export const LoginPage: React.FC = () => {
  const [loginMethod, setLoginMethod] = useState<'pin' | 'email'>('pin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { user, signIn, signInWithPin, isLoading, error, clearError, signOut } = useAuthStore();

  // Clear any existing session on mount
  React.useEffect(() => {
    signOut();
  }, []);

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  const handlePinLogin = async (pin: string) => {
    clearError();
    await signInWithPin(pin);
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await signIn(email, password);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-saavi-500 rounded-2xl flex items-center justify-center mx-auto mb-4"
          >
            <Package className="w-10 h-10 text-white" />
          </motion.div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mojo Pick
          </h1>
          <p className="text-gray-600">
            Sign in to start picking orders
          </p>
        </div>

        {/* Login Forms */}
        <Card padding="lg">
          {loginMethod === 'pin' ? (
            <>
              <PINPad
                onSubmit={handlePinLogin}
                loading={isLoading}
                error={error}
              />
              
              {/* Login Method Toggle - moved below PIN pad */}
              <div className="flex mt-6 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setLoginMethod('pin')}
                  className={`
                    flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all
                    ${loginMethod === 'pin'
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  <User className="w-4 h-4 inline mr-2" />
                  PIN Login
                </button>
                
                <button
                  onClick={() => setLoginMethod('email')}
                  className={`
                    flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all
                    ${loginMethod === 'email'
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Login
                </button>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  icon={<Mail className="w-5 h-5 text-saavi-600" />}
                  placeholder="Enter your email"
                />
                
                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                />
                
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <p className="text-sm text-red-800">{error}</p>
                  </motion.div>
                )}
                
                <Button
                  type="submit"
                  loading={isLoading}
                  className="w-full"
                  size="lg"
                >
                  Sign In
                </Button>
              </form>
              
              {/* Login Method Toggle - moved below email form */}
              <div className="flex mt-6 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setLoginMethod('pin')}
                  className={`
                    flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all
                    ${loginMethod === 'pin'
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  <User className="w-4 h-4 inline mr-2" />
                  PIN Login
                </button>
                
                <button
                  onClick={() => setLoginMethod('email')}
                  className={`
                    flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all
                    ${loginMethod === 'email'
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Login
                </button>
              </div>
            </>
          )}
        </Card>

      </motion.div>
    </div>
  );
};